<script setup name="TvbYouTuBePlayer">
import { reactive, onMounted } from 'vue'

const props = defineProps({
  id: {
    type: [String, Number],
    default: ''
  },
  src: {
    type: String,
    default: ''
  },
  autoplay: {
    type: Boolean,
    default: false
  },
  mute: {
    type: Boolean,
    default: true
  },
  playerVars: {
    type: Object,
    default() {
      return {}
    }
  }
})

const emits = defineEmits(['stateChange'])

const youtube = reactive({
  embedCode: null,
  start: 0
})

let player = null

const initPlayer = ({ autoplay }) => {
  if (player) return
  emits('stateChange', {
    player: null,
    type: 'youtube',
    state: 'loading'
  })
  try {
    const _id = props.id ? '-' + props.id : '' // 保证 id 唯一
    let playTimeout = null
    player = new window.YT.Player(`youtube-video-container-${youtube.embedCode}${_id}`, {
      height: '100%',
      width: '100%',
      videoId: youtube.embedCode,
      playerVars: {
        autoplay: props.autoplay ? 1 : 0,
        playsinline: 1,
        modestbranding: 1,
        rel: 0,
        hl: 'zh-HK',
        start: youtube.start,
        ...props.playerVars
      },
      events: {
        onReady: ({ target }) => {
          player = target
          const { title } = player.getVideoData()
          if (!title) {
            onError({ code: 2, message: '请求包含无效的参数值', type: 'youtube' })
          } else if (props.autoplay || autoplay) {
            play({ mute: props.mute })
            playTimeout = setTimeout(() => play({ mute: props.mute }), 5000)
          }
        },
        onStateChange: ({ data, target }) => {
          player = target
          if (['ended', 'play', 'pause', 'loading'][data]) {
            if (playTimeout) clearTimeout(playTimeout)
            emits('stateChange', {
              player: target,
              type: 'youtube',
              state: ['ended', 'play', 'pause', 'loading'][data]
            })
          }
        },
        onError: () => {
          onError({ code: 2, message: '请求包含无效的参数值', type: 'youtube' })
          emits('stateChange', { player, type: 'youtube', state: 'error' })
        }
      }
    })
  } catch (error) {
    console.error(error)
    window.requestAnimationFrame(initPlayer)
  }
}

const play = () => {
  if (!player) initPlayer({ autoplay: true })
  if (player?.getPlayerState && player?.getPlayerState() !== 1) {
    player?.playVideo()
    emits('stateChange', {
      player,
      type: 'youtube',
      state: 'play'
    })
  }
  if (props?.mute && player?.mute) player?.mute()
}

const pause = () => {
  if (player?.getPlayerState && player?.getPlayerState() === 1) {
    try {
      player?.pauseVideo()
      emits('stateChange', {
        player,
        type: 'youtube',
        state: 'pause'
      })
    } catch (error) {
      console.error(error)
    }
  }
}

const onError = () => {}

onMounted(() => {
  let manifestUrl = location
  try {
    manifestUrl = new URL(props.src)
  } catch (error) {
    console.error(error)
  }
  youtube.embedCode = manifestUrl?.searchParams?.get('v')
  youtube.start = manifestUrl?.searchParams?.get('t')?.replace?.(/[^0-9]/gi, '') ?? 0
})

defineExpose({ play, pause, initPlayer })
</script>

<template>
  <div
    v-if="youtube.embedCode"
    class="tvb-youtube-player"
    :id="`youtube-video-container-${youtube.embedCode}${id ? '-' + id : ''}`"
  ></div>
</template>
